import { Formik } from 'formik';
import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ErrorMessage, ISelectOption, ModalWindow, SwitchDialogWindows } from 'ui-toolkit';
import { createInternalTransferAction } from '../../store/accounting/slices/createInternalTransferSlice';
import { IUnrestrictedInternalTransferCreateData } from '../../types/accounting/data/IUnrestrictedInternalTransferCreateData';
import { UnrestrictedInternalTransfersForm } from './UnrestrictedInternalTransfersForm';
import { unrestrictedInternalTransferCreateDataValidationSchema } from '../../types/accounting/data/unrestrictedInternalTransferCreateDataValidationSchema';
import { useEscrowFileByOrderId } from '../../store/accounting/useEscrowFileByOrderId';
import { useAppDispatch } from '../../store/store';
import { RootStore } from '../../store/types';
import { ISearchOrderData } from '../../ui-toolkit/orders/searchOrder/types/ISearchOrder';
import { useGetEscrowBankAccountsQuery } from 'API/accounting/AccountingApi';
import { IEscrowBankAccountData } from '../../types/accounting/data/IEscrowBankAccountData';
import { useHasFeatureFlag } from '../../selectors/useHasFeatureFlag';

export interface IUnrestrictedInternalTransferCreateContainerProps {
  onClose: () => void;
  onCreate: () => void;
}

export const UnrestrictedInternalTransferCreateContainer: React.FC<
  IUnrestrictedInternalTransferCreateContainerProps
> = ({ onClose, onCreate }) => {
  const dispatch = useAppDispatch();
  const { data: bankAccounts = [] } = useGetEscrowBankAccountsQuery();
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');

  const bankAccountOptions: ISelectOption<string>[] = useMemo(() => {
    return bankAccounts.map((account: IEscrowBankAccountData) => {
      const displayName = isZillowClosingEnabled ? account.nickname : account.spruceNickname;
      return {
        label: `${displayName} (${account.stateAbbreviation} - ${account.purpose}) ****${account.accountNumber.slice(
          account.accountNumber.length - 4
        )}`,
        value: account.id,
      };
    });
  }, [bankAccounts, isZillowClosingEnabled]);

  const { createInternalTransfer } = useSelector((state: RootStore) => state.accounting);
  const { fetchStatus, fetchError } = createInternalTransfer;

  const [selectedOrder, setSelectedOrder] = useState<ISearchOrderData | undefined>();

  const { data: escrowFile, fetchError: getEscrowFileError } = useEscrowFileByOrderId(selectedOrder?.id);

  const [internalTransferWasCreated, setInternalTransferWasCreated] = useState(false);

  const onContainerizedClose = () => {
    onClose();
    if (internalTransferWasCreated) onCreate();
  };

  return (
    <SwitchDialogWindows
      errorTitle={'There was an error creating the internal transfer'}
      isCompleted={fetchStatus.fetched}
      isError={!!fetchError}
      isLoading={fetchStatus.fetching}
      loadingTitle={'Creating Internal Transfer'}
      onCloseClick={onContainerizedClose}
      successTitle={'Internal Transfer was created.'}
    >
      <ModalWindow width={700} title="Create Internal Transfer" contentTextAlign="left" onClose={onContainerizedClose}>
        <Formik
          validationSchema={unrestrictedInternalTransferCreateDataValidationSchema}
          initialValues={{} as IUnrestrictedInternalTransferCreateData}
          onSubmit={async (values) => {
            if (selectedOrder && escrowFile) {
              dispatch(
                createInternalTransferAction({
                  amountCents: values.amountCents,
                  category: values.category,
                  note: values.note,
                  escrowFileId: escrowFile.id,
                  sourceAccountId: values.sourceAccount,
                  destinationAccountId: values.destinationAccount,
                  unrestricted: true,
                })
              );
              setInternalTransferWasCreated(true);
            } else
              throw Error(
                `Submit Error: ${!selectedOrder ? 'Order was not selected' : ''} ${
                  !escrowFile ? 'Escrow File was not found' : ''
                }`
              );
          }}
        >
          <>
            {getEscrowFileError && (
              <ErrorMessage
                message={`There was an error getting Escrow File for Order: ${
                  selectedOrder ? selectedOrder.id : undefined
                }`}
                error={getEscrowFileError}
              />
            )}
            <UnrestrictedInternalTransfersForm
              destinationAccounts={bankAccountOptions}
              escrowFile={escrowFile}
              onCancel={onClose}
              onOrderSelect={setSelectedOrder}
              sourceAccounts={bankAccountOptions}
            />
          </>
        </Formik>
      </ModalWindow>
    </SwitchDialogWindows>
  );
};
